import {Button, TextField, Typography} from "@mui/material";
import {DW_XS, PD_XXLG} from "shared/dimens";
import React, {ReactElement, useState} from "react";
import {App} from "./App";
import {DIALOG_FLAG_ANIM_SLIDE, DIALOG_FLAG_SHOW_CLOSE} from "shared/BaseApp";
import {CustomDialogContent} from "shared/Dialogs";
import {StyledBoxColumn, StyledBoxRow} from "shared/StyledComponents";
import {getMemberAuth} from "shared/auth";
import {Room, ROOM_CODE_LENGTH, RoomJoin, RoomJoins, Rooms} from "./types";
import {passkey} from "shared/passkey";
import {PathProps} from "../index";

const DELAY = 1;

function EnterRoomCodeView(props: { onRoomCodeEntered: (code: string) => void }): ReactElement {
  const [firstCode, setFirstCode] = useState<string>("");
  const [secondCode, setSecondCode] = useState<string>("");
  const code = firstCode + "-" + secondCode;
  const enabled = firstCode.length + secondCode.length >= ROOM_CODE_LENGTH;
  const onKeyDown = event => {
    if (enabled && event.keyCode === 13) {
      event.preventDefault();
      props.onRoomCodeEntered(code);
    }
  };
  return <CustomDialogContent
    style={{minWidth: DW_XS, width: null}}
    title="Enter room code"
    customView={
      <StyledBoxColumn>
        <StyledBoxRow style={{alignItems: "center"}}>
          <TextField
            autoFocus
            type="text"
            required
            onKeyDown={onKeyDown}
            inputProps={{
              style: {fontSize: "100%", fontWeight: "bold", flexGrow: 1, textAlign: "center"},
              maxLength: ROOM_CODE_LENGTH / 2,
            }}
            value={firstCode}
            onChange={event => setFirstCode(event.target.value?.trim().toUpperCase() || "")}/>
          <Typography style={{color: "white"}}> - </Typography>
          <TextField
            type="text"
            required
            onKeyDown={onKeyDown}
            inputProps={{
              style: {fontSize: "100%", fontWeight: "bold", flexGrow: 1, textAlign: "center"},
              maxLength: ROOM_CODE_LENGTH / 2,
            }}
            value={secondCode}
            onChange={event => setSecondCode(event.target.value?.trim().toUpperCase() || "")}/>
        </StyledBoxRow>
        <Button
          style={{
            paddingLeft: PD_XXLG,
            paddingRight: PD_XXLG
          }}
          disabled={code.length < ROOM_CODE_LENGTH + 1}
          variant="contained"
          onClick={() => props.onRoomCodeEntered(code)}>
          Join room
        </Button>
      </StyledBoxColumn>
    }/>;
}

export class NewRoomHelper {

  constructor(readonly path: PathProps, readonly creatorOrJoinerNickname: string) {
  }

  showEnterRoomCodeDialog() {
    App.CONTEXT.showDialog({flags: DIALOG_FLAG_SHOW_CLOSE | DIALOG_FLAG_ANIM_SLIDE}, () =>
      <EnterRoomCodeView onRoomCodeEntered={code => {
        App.CONTEXT.hideDialog();
        setTimeout(() => this.joinAndShowNewRoom(code), DELAY);
      }}/>
    );
  }

  async joinAndShowNewRoom(code: string) {
    const memberId = getMemberAuth().getMemberId();
    const room = await Rooms.getInstance().getOrLoadItem(code);
    if (!room) {
      App.CONTEXT.showToast("Room not found! Please try again.");
      return;
    }
    if (room.joinedBy && room.joinedBy !== memberId) {
      App.CONTEXT.showToast("Room is unavailable! Please try again.");
      return;
    }
    if (!room.joinedBy) {
      room.joinerNickname = this.creatorOrJoinerNickname;
      room.joinedBy = memberId;
      room.joinedAt = Date.now();
      room.joiner = getMemberAuth().member;
      await Rooms.getInstance().addListItem(room);
    }
    const join = await RoomJoins.getInstance().loadListItem(room.id);
    if (!join) {
      await RoomJoins.getInstance().addListItem(RoomJoin.createNew(room.id));
    }
    this.path.navigate("/r/" + room.id);
  }

  async createAndShowNewRoom() {
    const code = passkey({
      length: ROOM_CODE_LENGTH,
      type: "alphabetic_upper"
    });
    const room = new Room(
      code.substring(0, ROOM_CODE_LENGTH / 2) + "-" + code.substring(ROOM_CODE_LENGTH / 2, ROOM_CODE_LENGTH),
      getMemberAuth().getMemberId(),
      Date.now(),
      this.creatorOrJoinerNickname);
    await Rooms.getInstance().addListItem(room);
    await RoomJoins.getInstance().addListItem(RoomJoin.createNew(room.id));
    this.path.navigate("/r/" + room.id);
  }
}